import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { filter, map, Observable } from 'rxjs';

import { runtimeActions } from '@blaze-runtime/engine-data-access';
import {
  makeBorderStyles,
  makeContainerVisualEffectStyles,
  RenderElementContext,
} from '@blaze-shared/elements-domain';
import { type ElementSchema } from '@blaze-shared/schema-domain';
import { useEmitTracker } from '@blaze-shared/shared-util';

import { CanvasElementComponent } from '../../canvas-elements/element-component';
import { hasSlots } from '../../canvas-elements/element-model/element-props-guards';
import { FormGroupLikeRenderElementInstance } from '../../canvas-elements/models/render-element.models';
import { CanvasElementBaseComponent } from '../__base/base.component';

@UntilDestroy()
@Component({
  selector: 'be-form-group',
  templateUrl: './form-group.component.html',
  styleUrls: ['./form-group.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class FormGroupComponent
  extends CanvasElementBaseComponent<'formGroup'>
  implements
    CanvasElementComponent<'formGroup'>,
    OnChanges,
    OnInit,
    FormGroupLikeRenderElementInstance
{
  @Input() value!: Record<string, any>;

  private readonly outputEmitTracker = useEmitTracker();
  private readonly dataValue = this.getDataInput('value');

  noBindingMessage$: Observable<string | undefined>;

  slotStyles!: Record<string, string | undefined>;

  schema: ElementSchema | undefined;

  get slotElementContext(): { elementContext: RenderElementContext } {
    return {
      elementContext: {
        disableShadowEffect: true,
        dataContainer: 'form-group',
      },
    };
  }

  constructor() {
    super();

    this.noBindingMessage$ = this.element$.pipe(
      map((element) => {
        if (!this.isInEditor || !hasSlots(element)) {
          return;
        }
        const hasOneSlot = element.slots.ids.length === 1;
        const firstSlotId = element.slots.ids[0];
        if (
          !hasOneSlot ||
          firstSlotId === undefined ||
          element.slots[firstSlotId]?.children.length !== 0
        ) {
          return;
        }
        return 'Choose your primary table in the property panel.\nOptionally, add different components here.';
      }),
    );

    this.initMarginHandler((props) => props?.style?.general?.offset?.margin);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.props) {
      this.updateStyles();

      // NOTE the model is not bound -> create obj from props model and emit updated value
      if (!this.bindingResults?.['value']) {
        this.value = this.props?.content.model?.reduce((acc, curr) => {
          acc[curr.field] = curr.value;
          return acc;
        }, {} as any);
        this.updateOutput();
        return;
      }
    }

    // NOTE model is bound and the input has changed -> propagate value
    if (changes['value'] && this.bindingResults?.['value']) {
      this.updateOutput();
    }
  }

  ngOnInit(): void {
    if (!this.bindingResults?.['value']) {
      this.setDefaultEventSchema('change');
    }
    this.dataValue.schema$.pipe(filter(Boolean), untilDestroyed(this)).subscribe((schema) => {
      if (schema) {
        this.schema = this.createElementSchema('change', undefined, schema);
        this.setDefaultEventSchema('change', this.schema);
      }
    });

    this.store.dispatch(
      runtimeActions.updateElementMeta({
        elementId: this.elementId,
        meta: {
          dataFeatures: {
            elementType: 'formGroup',
          },
        },
      }),
    );
  }

  getInstanceValue(): Record<string, any> {
    return this.value;
  }

  valueUpdated(): void {
    this.outputEmitTracker.shouldEmit(this.value);
  }

  validateValue(): void {
    // nothing to do
  }

  shouldEmitOutput(): boolean {
    return this.outputEmitTracker.hasChanges();
  }

  private updateOutput(): void {
    if (this.outputEmitTracker.shouldEmit(this.value)) {
      this.output.emit({
        eventType: 'change',
        data: this.value,
        isInitialData: false,
        schema: this.schema,
      });
    }
  }

  private updateStyles(): void {
    const { general, container } = this.props?.style || {};

    if (!general || !container) {
      return;
    }

    const slotStyles = {
      padding: general.offset.padding,
      ...makeContainerVisualEffectStyles(general.visualEffect),
      ...makeBorderStyles(container.outline, container.outlineWidth),
    };

    this.slotStyles = slotStyles;
  }
}
